import type { useNavigate } from '@tanstack/react-router';

import { useUiStore } from '@/features/settings/ui-store.js';
import {
  createConversation,
  resetChatStreamGuard,
} from '@/integration/services/integration-api.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';
import {
  runName,
  type CaseOutcome,
  type ReportBundle,
  type RunDetail,
} from '@/lib/api/quality-types.js';

import {
  fetchProject,
  fetchRunDetail,
  fetchReportBundle,
} from '@/lib/api/quality.js';
import { getWorkspace } from '@/lib/api/workspaces.js';
import {
  validateAgentActionHandoff,
  type AgentAction,
} from './agentActionHandoff.js';

import { buildRunCardRef, type RunActionMode } from './runCardRef.js';
import {
  analyzePrompt,
  buildExploratoryRunDigest,
  buildRunDigest,
  createTaskPrompt,
  exploratoryAnalyzePrompt,
  exploratoryCreateTaskPrompt,
  prepareActionPrompt,
  runChatTitle,
} from './runDigest.js';

type NavigateFn = ReturnType<typeof useNavigate>;

/** Resolve authorized run context before reserving a chat with an editable, unsent draft. */
export async function openInAgent(args: {
  detail: RunDetail;
  bundle: ReportBundle | undefined;
  mode: RunActionMode;
  selection?: {
    caseIds: string[];
    outcomes: CaseOutcome[];
    reportRevision: number;
  };
  navigate: NavigateFn;
}): Promise<void> {
  const { mode, navigate, selection } = args;
  const detail = await fetchRunDetail(args.detail.run.id);
  const expectedRevision =
    selection?.reportRevision ?? detail.run.report_revision;
  const bundle = await fetchReportBundle(detail.run.id, expectedRevision);
  if (
    detail.run.outcome_protocol_version === 1 &&
    (bundle?.coherence !== 'coherent' ||
      bundle.reportRevision !== expectedRevision)
  )
    throw new Error(
      'The current report changed or could not be loaded. Refresh the run and retry the Agent action.',
    );
  if (mode !== 'analyze' && mode !== 'create-task') {
    if (!selection)
      throw new Error(
        'Select at least one current case before preparing this action.',
      );
    const error = validateAgentActionHandoff(
      { ...selection, action: mode as AgentAction },
      detail,
      bundle,
    );
    if (error) throw new Error(error);
  }
  if (!detail.run.project_id)
    throw new Error('This run has no linked Quality project.');
  const project = await fetchProject(detail.run.project_id);
  if (project.id !== detail.run.project_id || !project.workspace_id) {
    throw new Error('This Quality project has no linked workspace.');
  }
  const workspace = await getWorkspace(project.workspace_id);
  if (workspace.id !== project.workspace_id || workspace.status !== 'active') {
    throw new Error('The run workspace is unavailable for a conversation.');
  }
  const label = runName(detail.run);
  const runId = detail.run.id;

  const exploratory = detail.run.run_kind === 'exploratory';
  const digest = exploratory
    ? buildExploratoryRunDigest(detail, bundle)
    : buildRunDigest(detail, bundle);
  const prompt = (() => {
    if (mode === 'analyze')
      return exploratory
        ? exploratoryAnalyzePrompt(digest, runId)
        : analyzePrompt(digest, runId);
    if (mode === 'create-task')
      return exploratory
        ? exploratoryCreateTaskPrompt(digest, runId)
        : createTaskPrompt(digest, runId);
    if (!selection)
      throw new Error(
        'Select at least one current case before preparing this action.',
      );
    return prepareActionPrompt(digest, {
      action: mode,
      runId,
      caseIds: selection.caseIds,
      outcomes: selection.outcomes,
      reportRevision: selection.reportRevision,
    });
  })();
  const text = prompt + buildRunCardRef(runId, label, mode);

  const conv = await createConversation({
    title: runChatTitle(detail, bundle, mode),
    workspaceId: project.workspace_id,
  });
  resetChatStreamGuard();
  const ui = useUiStore.getState();
  const session = useChatSessionStore.getState();
  session.setActiveConversationId(null);
  session.setStreamingConvId(null);
  ui.setPendingDraft(null);
  ui.setPendingAttachments([]);
  ui.setPendingConversationId(null);
  ui.setPendingFiles([]);
  ui.setPendingMic(false);
  ui.setPendingSkillIds([]);
  ui.setPendingSkill(null);
  ui.setPendingSkillsDirectoryId(null);
  ui.setPendingArtifactAttachments([]);
  ui.setPendingSharePointAttachments([]);
  ui.setPendingOneDriveAttachments([]);
  ui.setComposerPrefillAttachments([]);
  ui.setComposerPrefillSkillIds([]);
  ui.setComposerPrefillRestoresDraft(true);
  ui.setSelectedWorkspaceId(project.workspace_id);
  ui.setComposerPrefill(text);
  await navigate({ to: '/chats', search: { conversationId: conv.id } });
}
